import {
    Flex,
    Stack,
    Heading,
    Text,
    Fieldset,
} from '@chakra-ui/react';
import { useState, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm, SubmitHandler } from 'react-hook-form';
import { authService } from '@/service/AuthService';
import CancelButton from '@/components/buttons/CancelButton';
import SubmitButton from '@/components/buttons/SubmitButton';
import { InputGroup } from '@/components/ui/input-group';
import { Field } from '@/components/ui/field';
import { Avatar } from '@/components/ui/avatar';
import { Alert } from '@/components/ui/alert';
import { EmailIcon, LockIcon } from '@/components/icons/Icon';
import { PasswordInput } from '@/components/ui/password-input';
import PasswordStrengthIndicator from '@/components/PasswordStrengthIndicator';
import { FormInput } from '@/components/fields/FormInput';

interface ResetPasswordEntry {
    email: string;
    newPassword: string;
    confirmNewPassword: string;
}

const ResetPassword = () => {
    const { token: tokenParam } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const token = useMemo(() => tokenParam || searchParams.get('token') || '', [tokenParam, searchParams]);
    const initialEmail = useMemo(() => searchParams.get('email') || '', [searchParams]);

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<ResetPasswordEntry>({
        defaultValues: {
            email: initialEmail,
            newPassword: '',
            confirmNewPassword: ''
        }
    });

    const [errorMessage, setErrorMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const onSubmitReset: SubmitHandler<ResetPasswordEntry> = (entry) => {
        if (!token) {
            setErrorMessage('Ungültiger oder fehlender Link.');
            return;
        }
        if (entry.newPassword !== entry.confirmNewPassword) {
            setErrorMessage('Die beiden Passwörter sind nicht identisch.');
            return;
        }
        setIsLoading(true);
        setErrorMessage('');
        authService.passwordReset(token, entry.email, entry.newPassword)
            .then(() => {
                setSuccess(true);
            })
            .catch(err => {
                console.error('password reset failed:', err);
                if (err.status === 401) {
                    setErrorMessage('Link abgelaufen oder ungültig. Bitte erneut anfordern.');
                } else if (err.status === 403) {
                    setErrorMessage('Der Link wurde bereits verwendet. Bitte erneut anfordern.');
                } else {
                    setErrorMessage('Ungültiger E-Mail.');
                }
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <>
            <Stack gap={8} mx={'auto'} maxW={'lg'} minH={{ base: '40vh', lg: '70vh' }} py={6} px={6}
                justify={'center'}
            >
                <Stack align={'center'}>
                    <Avatar bg="teal.500" />
                    <Heading fontSize={'4xl'} textAlign={'center'}>Passwort zurücksetzen</Heading>
                    <Text fontSize={'lg'} color={'gray.600'}>Setze dein neues Passwort mit dem zugesandten Link. 🔐</Text>
                </Stack>
                <form noValidate onSubmit={handleSubmit(onSubmitReset)}>
                    <Fieldset.Root size='md' maxW='md' invalid={errorMessage && errorMessage.length > 0} rounded={'lg'}
                        disabled={!token}
                        boxShadow={'lg'}
                        py='3rem'
                        px='2rem'
                    >
                        <Fieldset.ErrorText>
                            {errorMessage}
                        </Fieldset.ErrorText>
                        <Stack gap={4}>
                            {success && <Alert status='success'>Passwort gesetzt! Du kannst dich jetzt anmelden.</Alert>}
                            {!token && <Alert status='warning'>Der Link ist ungültig oder fehlt.</Alert>}
                            <FormInput
                                name="email"
                                label="E-Mail"
                                register={register}
                                error={errors.email}
                                readOnly={success || !token}
                                required
                                icon={EmailIcon}
                                autoFocus={true}
                                type="email"
                                rules={{ required: 'E-Mail ist erforderlich' }}
                            />
                            <Field id="newPassword" label='Neues Passwort' readOnly={success || !token} required invalid={!!errors.newPassword} errorText={errors.newPassword?.message}>
                                <InputGroup startElement={<LockIcon />} w='full'>
                                    <PasswordInput {...register('newPassword', { required: 'Passwort ist erforderlich' })} />
                                </InputGroup>
                            </Field>
                            <PasswordStrengthIndicator password={watch('newPassword')} shortScoreWord='zu kurz' scoreWords={['schwach', 'schwach', 'okay', 'gut', 'stark']} />
                            <Field id="confirmNewPassword" label='Neues Passwort wiederholen' readOnly={success || !token} required invalid={!!errors.confirmNewPassword} errorText={errors.confirmNewPassword?.message}>
                                <InputGroup startElement={<LockIcon />} w='full'>
                                    <PasswordInput {...register('confirmNewPassword', {
                                        required: 'Bitte wiederholen',
                                        validate: (value) => value === watch('newPassword') || 'Die beiden Passwörter sind nicht identisch.'
                                    })} />
                                </InputGroup>
                            </Field>
                            <Flex justifyContent='flex-end' gap='2' flexWrap='wrap'>
                                <CancelButton onClick={() => navigate('/')} />
                                {!success && token && <SubmitButton caption='Passwort setzen' isSubmitting={isLoading} />}
                            </Flex>
                        </Stack>
                    </Fieldset.Root>
                </form>
            </Stack>
        </>
    )
}

export default ResetPassword
